import { DateTime, DurationObjectUnits, DurationUnits, ToRelativeUnit, DateTimeUnit, Duration } from 'luxon';

type TimeUnit = keyof DurationObjectUnits;

/**
* This function calculates the difference between two dates in a specified unit.
* @param startDateStr The start date
* @param endDateStr The end date
* @param unit the unit of time to calculate the difference in (seconds, minutes, hours, days, weeks, months, years).
* @returns The difference between the two dates in the specified unit or an empty string if the input strings are invalid. 
*/
export const calculateTimeDiffBetweenTwoDates = (startDate: string, endDate: string, timeUnit: TimeUnit): number | undefined => {
    try {
        const startTime = parseIsoDateToDateTimeObject(startDate);
        const endTime = parseIsoDateToDateTimeObject(endDate);
        const diff = startTime ? endTime?.diff(startTime, timeUnit).toObject() : null;

        if (diff && Number.isFinite(diff?.[timeUnit])) {
            return Math.floor(diff[timeUnit] as number);   
        }
        return;
    } catch (error) {
        console.error('Error calculating the diff between two dates ---> ', error);
        return;
    }
};

/**
 * This function converts a Unix timestamp to a formatted time string.
 * @param timestamp The unix timestamp in milliseconds to convert
 * @param dateFormat The date time format string to to be used to format the timestamp input e.g 'hh:mm a, MMM d'; 'h:mm a'.
 * @returns The formatted date time string, or an empty string if the input timestamp is invalid. 
 */
export const convertUnixTimeStampToDate = (timestamp: number, format: string): string => {
    try {
        const dateTimeFromTS = DateTime.fromMillis(timestamp);
        const formattedDateTimeStr = dateTimeFromTS.toFormat(format);
        return formattedDateTimeStr;
    } catch (error) {
        console.error('Error converting Unix timestamp to formatted date time string ---> ', error);
        return '';
    }
};

/**
 * This function formats an ISO 8601 date string and returns a formatted date string. 
 * It will return an empty string in case there's an error parsing the date string
 * @param dateStr - The ISO 8601 date string to format
 * @param format - The format string
 * @param setZone - Override the zone with a fixed-offset zone specified in the ISO string itself, if it specifies one - default to true.
 * @param timezone - timezone to change the zone in which this DateTime object instance created from ISO string input will be expressed
 * @returns The formatted date string or an empty string in case there was an error parsing input date string
 */
export const parseAndFormatISO8601DateStr = (isoDateStr: string, format: string, setZone=true, timezone?: string): string => {
    try {
        let dateTime;

        if (setZone) {
            dateTime = DateTime.fromISO(isoDateStr, { setZone: setZone });
        } else if (timezone) {
            dateTime = DateTime.fromISO(isoDateStr).setZone(timezone);
        } else {
            dateTime = DateTime.fromISO(isoDateStr);
        }

        if (!dateTime?.isValid) {
            return '';
        }

        const formattedValidDateTime = dateTime.toFormat(format);
        return formattedValidDateTime;
    } catch (error) {
        console.error('Error parsing/formating ISO 8601 ---> ', error);
        return '';
    }
};

/**
 * This function parses a custom date/time by first creating a DateTime instance using the local time,then changes the instances
 * timezone to utc without adjusting the values of the hours,minutes, and seconds.
 * @param customDateString - The custom date string to parse
 * @param formatStr - The format to parse the custom date string to
 * @returns The ISO 8601 Date string, or an null should parsing fail.
 */
export const parseCustomDateString = (customDateString: string, formatStr: string): string | null => {
    try {
        const dateTime = DateTime.fromFormat(customDateString, formatStr).setZone('utc');

        if (!dateTime.isValid) {
            return null; 
        }
        return dateTime.toISO();
    } catch (error) {
        console.error('Failed to parse custom date str ---> ', error);
        return null;
    }
};

/**
 * This function creates an DateTime Object from ISO date/time strings.
 * @param isoDateStr - The ISO 8601 date string
 * @returns A Luxon DateTime object
 */
export const parseIsoDateToDateTimeObject = (isoDateStr: string): DateTime | undefined => {
    try {
        const dateTimeObj = DateTime.fromISO(isoDateStr);
        return dateTimeObj;
    } catch (error) {
        console.error('Error Parsing ISO date string to DateTime Object --->', error);
        return;
    }
};

/**
 * This function takes a Unix timestamp in sec and converts to an ISO 8601 string.
 * 
 * @param unixTimeStamp - The Unix timestamp (sec) to be converted.
 * @returns An ISO 8601 string representation of the input Unix timestamp in UTC.
 */
export const convertUnixToISOString = (unixTimeStamp: number): string | null => {
    try {
        const dateObjFromTS = DateTime.fromSeconds(unixTimeStamp);
        const utcDateTime = dateObjFromTS.toUTC();
        const dateIsoString = utcDateTime.toISO();
        
        return dateIsoString;
        
    } catch (error) {
        console.error('Error Converting Unix timestamp to ISO string --->', error);
        return null;
    }
};

/**
 * This function takes a Unix timestamp in milliseconds and converts to human readable time string e.g 2 days ago, 5 hours ago etc.
 * 
 * @param unixTimestamp - The Unix timestamp (in milliseconds).
 * @returns A human readable string that represents the time between the input unixTimestamp and the current time.
 */
export const convertUnixTimeStampToRelativeTime = (unixTimestamp: number): string | null => {
    try {
        const dateTime = DateTime.fromMillis(unixTimestamp);

        // adjust both the input unixTimestamp and the current time to the local equivalents.
        const humanReadableRelativeTimeStr = dateTime.toRelative({ base: DateTime.now() });
        return humanReadableRelativeTimeStr;
    } catch (error) {
        console.error('Error Converting Unix timestamp to Human readable relative time string --->', error);
        return null;
    }
};

/**
 * This function two ISO date strings and determines if they have the same DateTime unit.
 * 
 * @param currDateStr - The first ISO date string to compare.
 * @param nextDateStr - The second ISO date string to compare.
 * @param unit - The unit of time to compare i.e 'minute', 'hour', 'day' etc. The default for this param is 'day'
 * @returns a boolean if the two ISO date strings have the same unit.
 */
export const isSameDateTimeUnit = (currDateStr: string, nextDateStr: string, dateTimeUnit: DateTimeUnit = 'day'): boolean => {
    try {
        const currDateObj = parseIsoDateToDateTimeObject(currDateStr);
        const nextDateObj = parseIsoDateToDateTimeObject(nextDateStr);
        
        if (currDateObj?.isValid && nextDateObj?.isValid) {
            return currDateObj.hasSame(nextDateObj, dateTimeUnit);
        }
        return false;
    } catch (error) {
        console.error('Error Comparing if date strings have same unit --->', error);
        return false;
    }
};

/**
 * This function formats the current date using the input date format string.
 * @param formatStr - The format string to format the current date to.
 * @returns formatted current date string or null if there's an error.
 */
export const formatCurrentDate = (formatStr: string): string | null => {
    try {
        return DateTime.now().toFormat(formatStr);
    } catch (error) {
        console.error('Error formatting current date string', error);
        return null;
    }
};

/**
 * This function parses a custom date/time string by first creating a DateTime instance using the local time zone (browser/system), then converts the instance
 * to UTC, preserving the same point in time in the UTC time zone
 * @param customDateTimeStr - The date/time string input to format
 * @param customDateFormat - The format of customDateTimeStr param that will be used when parsing the param
 * @returns the UTC DateTime instance of the customDateTimeStr
 */
export const parseCustomDateTimeToUTC = (customDateTimeStr: string, customDateFormat: string): DateTime | null => {
    try {
        return DateTime.fromFormat(customDateTimeStr, customDateFormat).toUTC();
    } catch (error) {
        console.error('Error parsing custom date to UTC', error);
        return null;
    }
};
